"use client"
import { useState, useEffect } from "react"
import {
  X,
  Save,
  Ban,
  Plus,
  Sigma,
  Trash2,
  CheckCircle,
  Wand2,
  ArrowLeft,
  Check,
  ImageIcon,
  Library,
  Upload,
  Loader2,
  AlertCircle,
} from "lucide-react"
import { renderLatex } from "./utils/latex-renderer"
import AddQuestionMathsExpression from "./AddQuestionMathExpression"
import { postAws } from "../postAws"
import { generatePresignedUrl, postTeacherQuestionbankImageFile } from "../postFileAws"

const QuestionEditorModal = ({ question, showModal, onClose, onSave, imageUris = [] }) => {
  const [editedQuestionText, setEditedQuestionText] = useState("")
  const [editedOptions, setEditedOptions] = useState({}) // was []
  const [editedReferences, setEditedReferences] = useState([])
  const [selectedCorrectAnswer, setSelectedCorrectAnswer] = useState("")
  const [selectedCorrectAnswers, setSelectedCorrectAnswers] = useState([])
  const [showMathExpressionBuilder, setShowMathExpressionBuilder] = useState(false)
  const [currentInputTarget, setCurrentInputTarget] = useState(null)

  const [editedPassage, setEditedPassage] = useState("")
  const [editedAssertion, setEditedAssertion] = useState("")
  const [editedReason, setEditedReason] = useState("")
  const [editedColumn1, setEditedColumn1] = useState([])
  const [editedColumn2, setEditedColumn2] = useState([])
  const [editedAnswer, setEditedAnswer] = useState("")

  // AI Rendering states
  const [hoveredField, setHoveredField] = useState(null)
  const [aiRenderingLoading, setAiRenderingLoading] = useState({})
  const [aiRenderingResponse, setAiRenderingResponse] = useState({})
  const [showAiResponse, setShowAiResponse] = useState({})

  // Added state for editing sub-questions
  const [editedSubQuestions, setEditedSubQuestions] = useState([])

  const [availableImages, setAvailableImages] = useState([])
  const [imagePresignedUrls, setImagePresignedUrls] = useState({})

  const [showImageLibrary, setShowImageLibrary] = useState(false)
  const [imageLibraryLoading, setImageLibraryLoading] = useState(false)
  const [currentImageTarget, setCurrentImageTarget] = useState(null)
  const [isUploadingImage, setIsUploadingImage] = useState(false)

  // compact, lowercase, no spaces; fallback "image"
  const slugifyRef = (s = "") =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(0, 32) || "image"

  const handleUploadToLibrary = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/jpeg,image/png" // images only

    input.onchange = async (e) => {
      const file = e.target.files?.[0]
      if (!file) return

      // build keypath from first reference text (or "image")
      const baseRef = (Array.isArray(editedReferences) && editedReferences[0]?.text) || "image"
      const keypath = slugifyRef(baseRef)

      // filename: <keypath>-<millis>.<ext>
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase()
      const filename = `${keypath}-${Date.now()}.${ext}`

      setIsUploadingImage(true)
      try {
        // upload -> get S3 uri back
        const { uri } = await postTeacherQuestionbankImageFile(file, filename, keypath)

        // put the *S3 URI* into library list (no dups)
        setAvailableImages((prev) => {
          const next = new Set(prev || [])
          next.add(uri)
          return Array.from(next)
        })

        // presign for preview in the grid
        const signed = await generatePresignedUrl(uri)
        setImagePresignedUrls((prev) => ({ ...(prev || {}), [uri]: signed }))
      } catch (err) {
        console.error("[upload][library] failed", err)
        alert("Failed to upload image. Please try again.")
      } finally {
        setIsUploadingImage(false)
        // clear file input for subsequent uploads
        input.value = ""
      }
    }

    input.click()
  }

  const extractImagesFromQuestion = (questionData) => {
    const images = []

    // Check if question has images property
    if (questionData?.images && Array.isArray(questionData.images)) {
      images.push(...questionData.images)
    }

    // Check for images in question text or other fields (looking for s3:// URIs)
    const checkForS3Uris = (text) => {
      if (!text) return []
      const s3UriRegex = /s3:\/\/[^\s,\]"'}]+/g
      return text.match(s3UriRegex) || []
    }

    // Check various fields for S3 URIs
    images.push(...checkForS3Uris(questionData?.question_text))
    images.push(...checkForS3Uris(questionData?.passage))
    images.push(...checkForS3Uris(questionData?.assertion))
    images.push(...checkForS3Uris(questionData?.reason))

    // Check options
    if (questionData?.options) {
      Object.values(questionData.options).forEach((option) => {
        images.push(...checkForS3Uris(option))
      })
    }

    // Remove duplicates and filter out empty strings
    return [...new Set(images)].filter((img) => img && img.trim())
  }

  useEffect(() => {
    if (showModal && imageUris.length > 0) {
      console.log("[v0] Received imageUris:", imageUris)
      setAvailableImages(imageUris)

      // Generate presigned URLs for all images
      const generatePresignedUrls = async () => {
        const urlMap = {}
        for (const uri of imageUris) {
          try {
            const presignedUrl = await generatePresignedUrl(uri)
            urlMap[uri] = presignedUrl
          } catch (error) {
            console.error("[v0] Failed to generate presigned URL for:", uri, error)
          }
        }
        setImagePresignedUrls(urlMap)
      }

      generatePresignedUrls()
    } else {
      setAvailableImages([])
      setImagePresignedUrls({})
    }
  }, [showModal, imageUris])

  const loadImageLibrary = async () => {
    setImageLibraryLoading(true)
    const presignedUrls = {}

    try {
      if ((availableImages?.length || 0) > 0) {
        for (const imageUri of availableImages) {
          try {
            const presignedUrl = await generatePresignedUrl(imageUri)
            presignedUrls[imageUri] = presignedUrl
            console.log("[v0] Generated presigned URL for:", imageUri)
          } catch (error) {
            console.error("[v0] Failed to generate presigned URL for:", imageUri, error)
            presignedUrls[imageUri] = null
          }
        }
        setImagePresignedUrls(presignedUrls)
      } else {
        // No images yet; ensure library opens with empty state and upload action
        setImagePresignedUrls({})
      }

      setShowImageLibrary(true)
    } catch (error) {
      console.error("[v0] Error loading image library:", error)
      alert("Failed to load image library. Please try again.")
    } finally {
      setImageLibraryLoading(false)
    }
  }

  const handleImageSelect = (imageUri) => {
    if (!currentImageTarget) return

    const imageMarkdown = `\n\n![Image](${imageUri})\n`

    const append = (setter) => setter((prev) => (prev || "") + imageMarkdown)

    if (currentImageTarget === "questionText") {
      append(setEditedQuestionText)
    } else if (currentImageTarget === "passage") {
      append(setEditedPassage)
    } else if (currentImageTarget === "assertion") {
      append(setEditedAssertion)
    } else if (currentImageTarget === "reason") {
      append(setEditedReason)
    } else if (/^option/i.test(currentImageTarget)) {
      // Examples: option1, option:a, optionA, option:b
      const rawPart = currentImageTarget.includes(":")
        ? currentImageTarget.split(":")[1]
        : currentImageTarget.replace(/^option/i, "")
      const raw = String(rawPart || "").trim()

      let letter = "a"
      if (/^[a-z]$/i.test(raw)) {
        letter = raw.toLowerCase()
      } else if (/^\d+$/.test(raw)) {
        const n = Math.max(1, Number.parseInt(raw, 10))
        letter = String.fromCharCode(96 + n) // 1->a, 2->b...
      }

      setEditedOptions((prev) => {
        const base = prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {}
        base[letter] = (base[letter] || "") + imageMarkdown
        return base
      })
    }

    setShowImageLibrary(false)
    setCurrentImageTarget(null)
  }

  const openImageLibrary = (target) => {
    setCurrentImageTarget(target)
    if (Object.keys(imagePresignedUrls).length === 0) {
      loadImageLibrary()
    } else {
      setShowImageLibrary(true)
    }
  }

  const convertImageUrisInContent = async (content) => {
    if (!content) return content

    let processedContent = content
    const s3UriRegex = /src="(s3:\/\/[^"]+)"/g
    const matches = [...content.matchAll(s3UriRegex)]

    for (const match of matches) {
      const s3Uri = match[1]
      if (imagePresignedUrls[s3Uri]) {
        processedContent = processedContent.replace(match[0], `src="${imagePresignedUrls[s3Uri]}"`)
      } else {
        try {
          const presignedUrl = await generatePresignedUrl(s3Uri)
          setImagePresignedUrls((prev) => ({ ...prev, [s3Uri]: presignedUrl }))
          processedContent = processedContent.replace(match[0], `src="${presignedUrl}"`)
        } catch (error) {
          console.error("[v0] Failed to convert image URI:", s3Uri, error)
        }
      }
    }

    return processedContent
  }

  const escapeHtml = (s = "") =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")

  // Turn ![alt](src) into an <img> tag (kept very small on purpose)
  const injectMarkdownImagesIntoHTML = (html = "") => {
    const input = html || ""

    // Correct Markdown image syntax: ![alt](url)
    const afterMarkdown = input.replace(
      /!\[([^\]]*)\]$$([^)]+)$$/g, // fixed regex and keep Markdown support
      (_m, alt, src) =>
        `<img alt="${escapeHtml(alt)}" src="${src}" style="max-width:100%;height:auto;display:inline-block;vertical-align:middle;margin:5px 0;border-radius:6px;" />`,
    )

    // Also convert bare presigned/image URLs inside text to clickable images (preview-only)
    // We skip matches already inside src="..." or href="..."
    const urlRegex = /(https?:\/\/[^\s<>"')]+?(?:\.(?:png|jpe?g|gif|webp|svg))(?:\?[^\s<>"')]*)?)/gi
    const afterUrls = afterMarkdown.replace(urlRegex, (match, _p1, offset, whole) => {
      const base = typeof whole === "string" ? whole : String(whole ?? "")
      const before = base.slice(Math.max(0, offset - 6), offset).toLowerCase()
      if (before.endsWith('src="') || before.endsWith('href="')) return match
      const safe = match.replace(/"/g, "&quot;")
      return `<a href="${safe}" target="_blank" rel="noopener noreferrer"><img alt="image" src="${safe}" style="max-width:100%;height:auto;display:inline-block;vertical-align:middle;margin:5px 0;border-radius:6px;" /></a>`
    })

    // Convert bare s3:// URIs to <img> (these will be swapped to presigned later)
    const s3Regex = /(s3:\/\/[^\s<>"')]+)/gi
    const afterS3 = afterUrls.replace(s3Regex, (match, _p1, offset, whole) => {
      const base = typeof whole === "string" ? whole : String(whole ?? "")
      const before = base.slice(Math.max(0, offset - 6), offset).toLowerCase()
      if (before.endsWith('src="') || before.endsWith('href="')) return match
      const safe = match.replace(/"/g, "&quot;")
      return `<img alt="image" src="${safe}" style="max-width:100%;height:auto;display:inline-block;vertical-align:middle;margin:5px 0;border-radius:6px;" />`
    })
    return afterS3
  }

  const renderRichHTML = (text = "") => {
    // Keep LaTeX rendering first, then inject images (from markdown or plain URLs)
    const latexHtml = renderLatex(text || "")
    return injectMarkdownImagesIntoHTML(latexHtml)
  }

  const PreviewContent = ({ content, className = "" }) => {
    const [processedContent, setProcessedContent] = useState(content)

    useEffect(() => {
      let cancelled = false
      ;(async () => {
        // 1) LaTeX render + Markdown → <img src="...">
        const html = renderRichHTML(content)
        // 2) Swap any s3:// src to presigned URLs (preview-only)
        const withPresigned = await convertImageUrisInContent(html)
        if (!cancelled) setProcessedContent(withPresigned)
      })()
      return () => {
        cancelled = true
      }
    }, [content, imagePresignedUrls])

    return <div className={className} dangerouslySetInnerHTML={{ __html: processedContent }} />
  }

  const detectQuestionType = (questionType, originalQuestionType) => {
    const provided = String(questionType || originalQuestionType || "")
      .toLowerCase()
      .trim()
    return provided || "mcq"
  }

  const actualQuestionType = detectQuestionType(question?.question_type, question?.original_question_type)

  const convertColumnObjectToText = (columnObj) => {
    if (!columnObj || typeof columnObj !== "object") return ""
    return Object.entries(columnObj)
      .map(([key, value]) => `${key}. ${value}`)
      .join("\n")
  }

  const convertTextToColumnObject = (textData) => {
    if (!textData || typeof textData !== "string") return {}

    const lines = textData.split("\n").filter((line) => line.trim())
    const columnObj = {}

    lines.forEach((line) => {
      const match = line.match(/^([A-Za-z])\.?\s*(.+)$/)
      if (match) {
        const [, key, value] = match
        columnObj[key.toUpperCase()] = value.trim()
      }
    })

    return columnObj
  }

  useEffect(() => {
    if (!question) return

    const initType = String(question?.question_type || question?.original_question_type || "mcq").toLowerCase()

    if (question.isParagraphGroup) {
      setEditedQuestionText(question.question_text || "")
      setEditedPassage(question.passage || "")
      setEditedOptions(question.options ? { ...question.options } : {})
      setEditedReferences(question.source_references ? [...question.source_references] : [])
      setEditedSubQuestions(question.questions ? [...question.questions] : [])
    } else {
      setEditedQuestionText(question.question_text || "")
      setEditedOptions(question.options ? { ...question.options } : {})
      setEditedReferences(question.source_references ? [...question.source_references] : [])
      setEditedPassage(question.passage || "")
    }

    setEditedAssertion(question.assertion || "")
    setEditedReason(question.reason || "")

    if (initType === "matrixmatch" || initType === "matchfollowing") {
      if (question.column_1 && typeof question.column_1 === "object") {
        setEditedColumn1(convertColumnObjectToText(question.column_1))
      } else if (question.column1) {
        setEditedColumn1(Array.isArray(question.column1) ? question.column1.join("\n") : question.column1)
      } else {
        const text = question.question_text || ""
        const column1Items = []
        const col1Match = text.match(/$$[A-Z, ]+$$/i)
        if (col1Match) {
          column1Items.push(
            ...col1Match[0]
              .replace(/[()]/g, "")
              .split(",")
              .map((item) => item.trim()),
          )
        }
        setEditedColumn1(column1Items.join("\n"))
      }

      if (question.column_2 && typeof question.column_2 === "object") {
        setEditedColumn2(convertColumnObjectToText(question.column_2))
      } else if (question.column2) {
        setEditedColumn2(Array.isArray(question.column2) ? question.column2.join("\n") : question.column2)
      } else {
        const text = question.question_text || ""
        const column2Items = []
        const col2Match = text.match(/$$[a-z, ]+$$/i)
        if (col2Match) {
          column2Items.push(
            ...col2Match[0]
              .replace(/[()]/g, "")
              .split(",")
              .map((item) => item.trim()),
          )
        }
        setEditedColumn2(column2Items.join("\n"))
      }
    }
  }, [question])

  const handleOptionChange = (key, value) => {
    setEditedOptions((prev) => {
      const newOptions = { ...prev, [key]: value }
      return newOptions
    })
  }

  const handleAddOption = (optionKey) => {
    setEditedOptions((prev) => ({
      ...prev,
      [optionKey]: "New option - please edit",
    }))
  }

  const handleRemoveOption = (optionKey) => {
    setEditedOptions((prev) => {
      const newOptions = { ...prev }
      delete newOptions[optionKey]

      // If the removed option was the correct answer, clear the selection
      if (selectedCorrectAnswer === optionKey) {
        setSelectedCorrectAnswer("")
      }

      return newOptions
    })
  }

  const handleCorrectAnswerChange = (optionKey) => {
    if (actualQuestionType === "multiplecorrect") {
      // Handle multiple selection with checkboxes
      setSelectedCorrectAnswers((prev) => {
        const upperKey = optionKey.toUpperCase()
        if (prev.includes(upperKey)) {
          // Remove if already selected
          return prev.filter((key) => key !== upperKey)
        } else {
          // Add if not selected
          return [...prev, upperKey].sort()
        }
      })
    } else {
      // Handle single selection with radio buttons
      setSelectedCorrectAnswer(optionKey)
    }
  }

  const handleReferenceChange = (index, value) => {
    const newReferences = [...editedReferences]
    newReferences[index] = { ...newReferences[index], text: value }
    setEditedReferences(newReferences)
  }

  const handleAddReference = () => {
    setEditedReferences((prev) => [...prev, { text: "", page: "" }])
  }

  const handleRemoveReference = (index) => {
    setEditedReferences((prev) => prev.filter((_, i) => i !== index))
  }

  // Added functions to handle sub-question editing
  const updateSubQuestion = (index, field, value) => {
    setEditedSubQuestions((prev) => {
      const updated = [...prev]
      if (!updated[index]) {
        updated[index] = {}
      }
      updated[index] = {
        ...updated[index],
        [field]: value,
      }
      return updated
    })
  }

  const updateSubQuestionOption = (subIndex, optionKey, value) => {
    setEditedSubQuestions((prev) => {
      const updated = [...prev]
      if (!updated[subIndex]) {
        updated[subIndex] = {}
      }
      if (!updated[subIndex].options) {
        updated[subIndex].options = {}
      }
      updated[subIndex].options[optionKey] = value
      return updated
    })
  }

  const addSubQuestionOption = (subIndex) => {
    const availableKeys = ["a", "b", "c", "d", "e", "f"]
    const currentOptions = editedSubQuestions[subIndex]?.options || {}
    const usedKeys = Object.keys(currentOptions)
    const nextKey = availableKeys.find((key) => !usedKeys.includes(key))

    if (nextKey) {
      updateSubQuestionOption(subIndex, nextKey, "New option - please edit")
    }
  }

  const removeSubQuestionOption = (subIndex, optionKey) => {
    setEditedSubQuestions((prev) => {
      const updated = [...prev]
      if (updated[subIndex] && updated[subIndex].options) {
        const newOptions = { ...updated[subIndex].options }
        delete newOptions[optionKey]
        updated[subIndex] = {
          ...updated[subIndex],
          options: newOptions,
        }

        if (updated[subIndex].correct_answer && updated[subIndex].correct_answer.includes(optionKey)) {
          updated[subIndex].correct_answer = updated[subIndex].correct_answer.filter((key) => key !== optionKey)
        }
      }
      return updated
    })
  }

  const handleSubQuestionCorrectAnswer = (subIndex, optionKey) => {
    setEditedSubQuestions((prev) => {
      const updated = [...prev]
      if (!updated[subIndex]) {
        updated[subIndex] = {}
      }

      // For comprehension sub-questions, typically single correct answer
      updated[subIndex].correct_answer = [optionKey]

      return updated
    })
  }

  const handleSave = () => {
    const columnData = {}

    if (actualQuestionType === "matrixmatch" || actualQuestionType === "matchfollowing") {
      if (editedColumn1) {
        columnData.column_1 = convertTextToColumnObject(editedColumn1)
      }
      if (editedColumn2) {
        columnData.column_2 = convertTextToColumnObject(editedColumn2)
      }
    }

    let correctAnswerValue
    if (actualQuestionType === "multiplecorrect") {
      correctAnswerValue = selectedCorrectAnswers.length > 0 ? selectedCorrectAnswers : ""
    } else {
      correctAnswerValue = selectedCorrectAnswer
    }

    const updatedQuestion = {
      ...question,
      question_text: editedQuestionText,
      options: editedOptions,
      source_references: editedReferences,
      correctAnswer: correctAnswerValue,
      passage: editedPassage,
      assertion: editedAssertion,
      reason: editedReason,
      answer: editedAnswer,
      ...columnData,
    }

    // Handle paragraph groups
    if (question?.isParagraphGroup) {
      updatedQuestion.passage = editedPassage
      updatedQuestion.questions = editedSubQuestions.filter((sq) => sq.question_text && sq.question_text.trim())
      updatedQuestion.isParagraphGroup = true
    }

    if (actualQuestionType === "assertionreasoning") {
      updatedQuestion.assertion = editedAssertion
      updatedQuestion.reason = editedReason
    }

    if (actualQuestionType === "matrixmatch" || actualQuestionType === "matchfollowing") {
      Object.assign(updatedQuestion, columnData)
    }

    if (
      actualQuestionType === "integer" ||
      actualQuestionType === "fillinblanks" ||
      actualQuestionType === "shortanswer"
    ) {
      updatedQuestion.answer = editedAnswer
    }

    // Keep an explicit list of S3 image URIs in the payload
    const s3Images = extractImagesFromQuestion(updatedQuestion)
    updatedQuestion.images = Array.from(new Set(s3Images))
    console.log("[v0] Saving question with data:", updatedQuestion)

    onSave(updatedQuestion)
    onClose()
  }

  const openMathExpressionBuilder = (target) => {
    setCurrentInputTarget(target)
    setShowMathExpressionBuilder(true)
  }

  const handleInsertMathExpression = (latexString) => {
    if (!currentInputTarget) return

    if (currentInputTarget === "questionText") {
      setEditedQuestionText((prev) => prev + latexString)
    } else if (currentInputTarget === "passage") {
      setEditedPassage((prev) => prev + latexString)
    } else if (currentInputTarget === "assertion") {
      setEditedAssertion((prev) => prev + latexString)
    } else if (currentInputTarget === "reason") {
      setEditedReason((prev) => prev + latexString)
    } else if (currentInputTarget === "answer") {
      setEditedAnswer((prev) => prev + latexString)
    } else if (currentInputTarget === "column1") {
      setEditedColumn1((prev) => prev + latexString)
    } else if (currentInputTarget === "column2") {
      setEditedColumn2((prev) => prev + latexString)
    } else if (currentInputTarget.startsWith("option_")) {
      const optionKey = currentInputTarget.replace("option_", "")
      setEditedOptions((prev) => ({
        ...prev,
        [optionKey]: (prev[optionKey] || "") + latexString,
      }))
    } else if (currentInputTarget.startsWith("reference_")) {
      const refIndex = Number.parseInt(currentInputTarget.replace("reference_", ""), 10)
      setEditedReferences((prev) => {
        const newRefs = [...prev]
        if (newRefs[refIndex]) {
          newRefs[refIndex] = { ...newRefs[refIndex], text: newRefs[refIndex].text + latexString }
        }
        return newRefs
      })
    }

    setShowMathExpressionBuilder(false)
    setCurrentInputTarget(null)
  }

  // AI Rendering Functions
  const handleAiRender = async (fieldType) => {
    let currentContent = ""

    // Get current content based on field type
    if (fieldType === "questionText") {
      currentContent = editedQuestionText
    } else if (fieldType === "passage") {
      currentContent = editedPassage
    } else if (fieldType === "assertion") {
      currentContent = editedAssertion
    } else if (fieldType === "reason") {
      currentContent = editedReason
    } else if (fieldType === "answer") {
      currentContent = editedAnswer
    } else if (fieldType === "column1") {
      currentContent = editedColumn1
    } else if (fieldType === "column2") {
      currentContent = editedColumn2
    } else if (fieldType.startsWith("option_")) {
      const optionKey = fieldType.replace("option_", "")
      currentContent = editedOptions[optionKey] || ""
    }

    if (!currentContent.trim()) return

    setAiRenderingLoading((prev) => ({ ...prev, [fieldType]: true }))

    try {
      const payload = {
        request: "fix-latex",
        body: {
          rawText: currentContent,
        },
      }

      console.log("AI Rendering payload:", payload)
      const data = await postAws("https://api-dev.aurait.ai/QuestionBank", "POST", payload)
      console.log("AI Rendering response:", data)

      if (data && data.statusCode === 200 && data.fixedLatex) {
        // Handle double backslashes properly - the server returns \\( but we need \(
        let fixedLatex = data.fixedLatex

        // Convert double backslashes to single backslashes for proper LaTeX rendering
        fixedLatex = fixedLatex.replace(/\\\\/g, "\\")

        console.log("Processed fixedLatex:", fixedLatex)

        setAiRenderingResponse((prev) => ({
          ...prev,
          [fieldType]: fixedLatex,
        }))
        setShowAiResponse((prev) => ({ ...prev, [fieldType]: true }))
      } else {
        console.error("AI rendering failed:", data)
        alert("Failed to render with AI. Please try again.")
      }
    } catch (error) {
      console.error("AI rendering error:", error)
      alert("Failed to render with AI. Please try again.")
    } finally {
      setAiRenderingLoading((prev) => ({ ...prev, [fieldType]: false }))
    }
  }

  const acceptAiResponse = (fieldType) => {
    const response = aiRenderingResponse[fieldType]

    if (fieldType === "questionText") {
      setEditedQuestionText(response)
    } else if (fieldType === "passage") {
      setEditedPassage(response)
    } else if (fieldType === "assertion") {
      setEditedAssertion(response)
    } else if (fieldType === "reason") {
      setEditedReason(response)
    } else if (fieldType === "answer") {
      setEditedAnswer(response)
    } else if (fieldType === "column1") {
      setEditedColumn1(response)
    } else if (fieldType === "column2") {
      setEditedColumn2(response)
    } else if (fieldType.startsWith("option_")) {
      const optionKey = fieldType.replace("option_", "")
      setEditedOptions((prev) => ({ ...prev, [optionKey]: response }))
    }

    setShowAiResponse((prev) => ({ ...prev, [fieldType]: false }))
    setAiRenderingResponse((prev) => ({ ...prev, [fieldType]: "" }))
  }

  const rejectAiResponse = (fieldType) => {
    setShowAiResponse((prev) => ({ ...prev, [fieldType]: false }))
    setAiRenderingResponse((prev) => ({ ...prev, [fieldType]: "" }))
  }

  const getAvailableOptions = () => {
    const allOptions = ["a", "b", "c", "d"]
    const existingOptions = Object.keys(editedOptions || {})
    return allOptions.filter((opt) => !existingOptions.includes(opt))
  }

  const canAddMoreOptions = () => {
    return Object.keys(editedOptions || {}).length < 4
  }

  const ImageLibraryButton = ({ fieldType, className = "" }) => {
    return (
      <button
        type="button"
        onClick={() => openImageLibrary(fieldType)}
        disabled={imageLibraryLoading}
        className={`absolute p-1 rounded-full bg-gradient-to-r from-blue-500 to-green-500 text-white hover:from-blue-600 hover:to-green-600 transition-all duration-200 shadow-lg ${className}`}
        title="Insert Image from Library"
        style={{
          opacity: hoveredField === fieldType ? 1 : 0,
          transform: hoveredField === fieldType ? "scale(1)" : "scale(0.8)",
          transition: "opacity 0.2s, transform 0.2s",
        }}
      >
        {imageLibraryLoading ? (
          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
        ) : (
          <ImageIcon className="h-4 w-4" />
        )}
      </button>
    )
  }

  const AIRenderButton = ({ fieldType, className = "" }) => {
    if (showAiResponse[fieldType]) return null

    return (
      <button
        type="button"
        onClick={() => handleAiRender(fieldType)}
        disabled={aiRenderingLoading[fieldType]}
        className={`absolute p-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg ${className}`}
        title="Render with AI"
        style={{
          opacity: hoveredField === fieldType ? 1 : 0,
          transform: hoveredField === fieldType ? "scale(1)" : "scale(0.8)",
          transition: "opacity 0.2s, transform 0.2s",
        }}
      >
        {aiRenderingLoading[fieldType] ? (
          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
        ) : (
          <Wand2 className="h-4 w-4" />
        )}
      </button>
    )
  }

  // AI Response Modal Component
  const AIResponseModal = ({ fieldType }) => {
    if (!showAiResponse[fieldType]) return null

    const response = aiRenderingResponse[fieldType]

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
          <div className="p-4 border-b bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <h3 className="text-lg font-semibold">AI Rendered Content</h3>
            <p className="text-sm opacity-90">Review the AI-rendered content below</p>
          </div>

          <div className="p-6 max-h-96 overflow-y-auto">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Original Content:</label>
              <div className="p-3 bg-gray-50 rounded border text-sm text-gray-600">
                {fieldType === "questionText"
                  ? editedQuestionText
                  : fieldType === "passage"
                    ? editedPassage
                    : fieldType === "assertion"
                      ? editedAssertion
                      : fieldType === "reason"
                        ? editedReason
                        : fieldType === "answer"
                          ? editedAnswer
                          : fieldType === "column1"
                            ? editedColumn1
                            : fieldType === "column2"
                              ? editedColumn2
                              : fieldType.startsWith("option_")
                                ? editedOptions[fieldType.replace("option_", "")]
                                : ""}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">AI Rendered Content:</label>
              <div className="p-3 bg-blue-50 rounded border">
                <div dangerouslySetInnerHTML={{ __html: renderLatex(response) }} />
              </div>
            </div>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
            <button
              onClick={() => rejectAiResponse(fieldType)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Go Back</span>
            </button>
            <button
              onClick={() => acceptAiResponse(fieldType)}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Check className="h-4 w-4" />
              <span>Accept & Use</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderLibraryButton = () => {
    return (
      <button
        onClick={() => {
          console.log("[v0] Opening image library from main button")
          setCurrentImageTarget("questionText")
          if (Object.keys(imagePresignedUrls || {}).length === 0) {
            loadImageLibrary()
          } else {
            setShowImageLibrary(true)
          }
        }}
        className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
      >
        <ImageIcon className="h-4 w-4" />
        Library ({availableImages?.length || 0})
      </button>
    )
  }

  const renderImageLibraryModal = () => {
    if (!showImageLibrary) return null

    const imageCount = availableImages?.length || 0

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              Image Library ({imageCount} {imageCount === 1 ? "image" : "images"})
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleUploadToLibrary}
                disabled={isUploadingImage}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50 disabled:opacity-60"
                title="Upload a JPG/PNG to your library"
              >
                {isUploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>{isUploadingImage ? "Uploading…" : "Upload image"}</span>
              </button>
              <button
                onClick={() => {
                  setShowImageLibrary(false)
                  setCurrentImageTarget(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {!currentImageTarget && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 mb-2">Select where to insert the image:</p>
              <select
                onChange={(e) => {
                  console.log("[v0] Target selected:", e.target.value)
                  setCurrentImageTarget(e.target.value)
                }}
                className="w-full p-2 border rounded-md text-sm"
                defaultValue=""
              >
                <option value="" disabled>
                  Choose a field...
                </option>
                <option value="questionText">Question Text</option>
                <option value="passage">Passage</option>
                <option value="assertion">Assertion</option>
                <option value="reason">Reason</option>
                <option value="answer">Answer</option>
                <option value="column1">Column 1</option>
                <option value="column2">Column 2</option>
                <option value="option_A">Option A</option>
                <option value="option_B">Option B</option>
                <option value="option_C">Option C</option>
                <option value="option_D">Option D</option>
              </select>
            </div>
          )}

          {currentImageTarget && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Click any image to insert it into: <strong>{getTargetDisplayName(currentImageTarget)}</strong>
              </p>
            </div>
          )}

          {imageCount === 0 ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center bg-gray-50">
              <ImageIcon className="h-10 w-10 mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-600 mb-4">No images in your library yet.</p>
              <button
                type="button"
                onClick={handleUploadToLibrary}
                disabled={isUploadingImage}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50 disabled:opacity-60"
                title="Upload a JPG/PNG to your library"
              >
                {isUploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>{isUploadingImage ? "Uploading…" : "Upload image"}</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {availableImages.map((imageUri, index) => (
                <div key={imageUri} className="border rounded-lg overflow-hidden bg-gray-50">
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    {imagePresignedUrls[imageUri] ? (
                      <img
                        src={
                          imagePresignedUrls[imageUri] ||
                          "/placeholder.svg?height=300&width=300&query=placeholder%20image" ||
                          "/placeholder.svg"
                        }
                        alt={`Image ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleImageSelect(imageUri)}
                      />
                    ) : (
                      <div className="text-center p-4">
                        <ImageIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-xs text-gray-500">Loading...</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-gray-600 truncate" title={imageUri}>
                      {imageUri.split("/").pop()}
                    </p>
                    <button
                      onClick={() => {
                        console.log("[v0] Select Image button clicked for:", imageUri)
                        handleImageSelect(imageUri)
                      }}
                      disabled={!imagePresignedUrls[imageUri] || !currentImageTarget}
                      className="mt-2 w-full bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      {!currentImageTarget
                        ? "Select target first"
                        : imagePresignedUrls[imageUri]
                          ? "Select Image"
                          : "Loading..."}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const getTargetDisplayName = (target) => {
    switch (target) {
      case "questionText":
        return "Question Text"
      case "passage":
        return "Passage"
      case "assertion":
        return "Assertion"
      case "reason":
        return "Reason"
      case "answer":
        return "Answer"
      case "column1":
        return "Column 1"
      case "column2":
        return "Column 2"
      default:
        if (target.startsWith("option_")) {
          const optionKey = target.replace("option_", "").toUpperCase()
          return `Option ${optionKey}`
        } else if (target.startsWith("reference_")) {
          const refIndex = Number.parseInt(target.replace("reference_", ""), 10) + 1
          return `Reference ${refIndex}`
        }
        return "Unknown Target"
    }
  }

  const renderQuestionTypeFields = () => {
    const questionType = actualQuestionType?.toLowerCase()

    switch (questionType) {
      case "comprehension":
      case "paragraph":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Passage/Paragraph</label>
              <div
                className="relative"
                onMouseEnter={() => setHoveredField("passage")}
                onMouseLeave={() => setHoveredField(null)}
              >
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32"
                  value={editedPassage}
                  onChange={(e) => setEditedPassage(e.target.value)}
                  placeholder="Enter passage or paragraph text"
                />
                <button
                  type="button"
                  onClick={() => openMathExpressionBuilder("passage")}
                  className="absolute top-2 right-2 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                  title="Add Special Symbols"
                >
                  <Sigma className="h-5 w-5" />
                </button>
                <AIRenderButton fieldType="passage" className="top-2 right-12" />
                <ImageLibraryButton fieldType="passage" className="top-2 right-[88px]" />
              </div>
            </div>

            {question?.isParagraphGroup && question.questions && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub-Questions ({editedSubQuestions.length})
                </label>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {editedSubQuestions.map((subQuestion, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-sm font-medium text-gray-600">
                          Q{subQuestion.sub_question_number || index + 1}:
                        </span>
                      </div>

                      {/* Made sub-question text editable */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Question Text</label>
                        <textarea
                          className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={subQuestion.question_text || ""}
                          onChange={(e) => updateSubQuestion(index, "question_text", e.target.value)}
                          rows={2}
                        />
                      </div>

                      {subQuestion.options && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">Options</label>
                          <div className="space-y-2">
                            {Object.entries(subQuestion.options).map(([key, value]) => (
                              <div key={key} className="space-y-1">
                                {/* Option Input */}
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs font-medium text-gray-600 w-6">{key}.</span>
                                  <input
                                    type="text"
                                    className="flex-1 p-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                    value={value || ""}
                                    onChange={(e) => updateSubQuestionOption(index, key, e.target.value)}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeSubQuestionOption(index, key)}
                                    className="p-1 text-red-500 hover:text-red-700"
                                    title="Remove option"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>

                                <div className="flex items-center space-x-2 ml-6">
                                  <input
                                    type="radio"
                                    id={`sub-correct-${index}-${key}`}
                                    name={`subCorrectAnswer-${index}`}
                                    value={key}
                                    checked={subQuestion.correct_answer && subQuestion.correct_answer.includes(key)}
                                    onChange={() => handleSubQuestionCorrectAnswer(index, key)}
                                    className="h-3 w-3 text-green-600 focus:ring-green-500 border-gray-300"
                                  />
                                  <label
                                    htmlFor={`sub-correct-${index}-${key}`}
                                    className={`text-xs cursor-pointer ${
                                      subQuestion.correct_answer && subQuestion.correct_answer.includes(key)
                                        ? "text-green-700 font-medium"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    {subQuestion.correct_answer && subQuestion.correct_answer.includes(key) ? (
                                      <span className="flex items-center space-x-1">
                                        <CheckCircle className="h-3 w-3" />
                                        <span>Correct</span>
                                      </span>
                                    ) : (
                                      "Mark as correct"
                                    )}
                                  </label>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addSubQuestionOption(index)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              + Add Option
                            </button>
                          </div>

                          {Object.keys(subQuestion.options).length > 0 && (
                            <div
                              className={`mt-2 p-2 rounded text-xs border ${
                                subQuestion.correct_answer && subQuestion.correct_answer.length > 0
                                  ? "bg-green-50 border-green-200 text-green-700"
                                  : "bg-yellow-50 border-yellow-200 text-yellow-700"
                              }`}
                            >
                              {subQuestion.correct_answer && subQuestion.correct_answer.length > 0 ? (
                                <span className="flex items-center space-x-1">
                                  <CheckCircle className="h-3 w-3" />
                                  <span>Correct answer: {subQuestion.correct_answer.join(", ").toUpperCase()}</span>
                                </span>
                              ) : (
                                <span className="flex items-center space-x-1">
                                  <AlertCircle className="h-3 w-3" />
                                  <span>Please select the correct answer</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )

      case "assertionreasoning":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assertion (A)</label>
              <div
                className="relative"
                onMouseEnter={() => setHoveredField("assertion")}
                onMouseLeave={() => setHoveredField(null)}
              >
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20"
                  value={editedAssertion}
                  onChange={(e) => setEditedAssertion(e.target.value)}
                  placeholder="Enter assertion statement"
                />
                <button
                  type="button"
                  onClick={() => openMathExpressionBuilder("assertion")}
                  className="absolute top-2 right-2 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                  title="Add Special Symbols"
                >
                  <Sigma className="h-5 w-5" />
                </button>
                <AIRenderButton fieldType="assertion" className="top-2 right-12" />
                <ImageLibraryButton fieldType="assertion" className="top-2 right-[88px]" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason (R)</label>
              <div
                className="relative"
                onMouseEnter={() => setHoveredField("reason")}
                onMouseLeave={() => setHoveredField(null)}
              >
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20"
                  value={editedReason}
                  onChange={(e) => setEditedReason(e.target.value)}
                  placeholder="Enter reason statement"
                />
                <button
                  type="button"
                  onClick={() => openMathExpressionBuilder("reason")}
                  className="absolute top-2 right-2 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                  title="Add Special Symbols"
                >
                  <Sigma className="h-5 w-5" />
                </button>
                <AIRenderButton fieldType="reason" className="top-2 right-12" />
                <ImageLibraryButton fieldType="reason" className="top-2 right-[88px]" />
              </div>
            </div>
          </div>
        )

      case "matrixmatch":
      case "matchfollowing":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Column 1</label>
              <div
                className="relative"
                onMouseEnter={() => setHoveredField("column1")}
                onMouseLeave={() => setHoveredField(null)}
              >
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32"
                  value={editedColumn1}
                  onChange={(e) => setEditedColumn1(e.target.value)}
                  placeholder="Enter items for column 1 (one per line)"
                />
                <button
                  type="button"
                  onClick={() => openMathExpressionBuilder("column1")}
                  className="absolute top-2 right-2 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                  title="Add Special Symbols"
                >
                  <Sigma className="h-5 w-5" />
                </button>
                <AIRenderButton fieldType="column1" className="top-2 right-12" />
                <ImageLibraryButton fieldType="column1" className="top-2 right-[88px]" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Column 2</label>
              <div
                className="relative"
                onMouseEnter={() => setHoveredField("column2")}
                onMouseLeave={() => setHoveredField(null)}
              >
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32"
                  value={editedColumn2}
                  onChange={(e) => setEditedColumn2(e.target.value)}
                  placeholder="Enter items for column 2 (one per line)"
                />
                <button
                  type="button"
                  onClick={() => openMathExpressionBuilder("column2")}
                  className="absolute top-2 right-2 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                  title="Add Special Symbols"
                >
                  <Sigma className="h-5 w-5" />
                </button>
                <AIRenderButton fieldType="column2" className="top-2 right-12" />
                <ImageLibraryButton fieldType="column2" className="top-2 right-[88px]" />
              </div>
            </div>
          </div>
        )

      case "shortanswer":
      case "integer":
      case "fillinblanks":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Expected Answer</label>
            <div
              className="relative"
              onMouseEnter={() => setHoveredField("answer")}
              onMouseLeave={() => setHoveredField(null)}
            >
              <input
                type="text"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={editedAnswer}
                onChange={(e) => setEditedAnswer(e.target.value)}
                placeholder="Enter expected answer (optional)"
              />
              <button
                type="button"
                onClick={() => openMathExpressionBuilder("answer")}
                className="absolute top-1/2 -translate-y-1/2 right-2 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                title="Add Special Symbols"
              >
                <Sigma className="h-5 w-5" />
              </button>
              <AIRenderButton fieldType="answer" className="top-1/2 -translate-y-1/2 right-12" />
              <ImageLibraryButton fieldType="answer" className="top-1/2 -translate-y-1/2 right-[88px]" />
            </div>
          </div>
        )

      default:
        return null
    }
  }

  const renderQuestionTypePreview = () => {
    const questionType = actualQuestionType?.toLowerCase()

    switch (questionType) {
      case "comprehension":
      case "paragraph":
        return (
          <div className="space-y-4">
            {editedPassage && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Passage Preview</label>
                <PreviewContent
                  content={editedPassage}
                  className="p-3 bg-blue-50 rounded border min-h-[80px] border-l-4 border-blue-500"
                />
              </div>
            )}

            {question?.isParagraphGroup && question.questions && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub-Questions Preview ({question.questions.length})
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {question.questions.map((subQuestion, index) => (
                    <div key={index} className="p-2 bg-white rounded border">
                      <div className="flex items-start space-x-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                          {subQuestion.sub_question_number || index + 1}
                        </span>
                        <div className="text-sm text-gray-700 flex-1">{subQuestion.question_text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )

      case "assertionreasoning":
        return (
          (editedAssertion || editedReason) && (
            <div className="space-y-3">
              {editedAssertion && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assertion (A) Preview</label>
                  <PreviewContent content={editedAssertion} className="p-3 bg-blue-50 rounded border" />
                </div>
              )}
              {editedReason && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason (R) Preview</label>
                  <PreviewContent content={editedReason} className="p-3 bg-green-50 rounded border" />
                </div>
              )}
            </div>
          )
        )

      case "matrixmatch":
      case "matchfollowing":
        return (
          (editedColumn1.length > 0 || editedColumn2.length > 0) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Matching Preview</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-gray-600 mb-2">Column 1</h4>
                  <div className="space-y-1">
                    {editedColumn1.split("\n").map((item, index) => (
                      <div key={index} className="p-2 bg-blue-50 rounded text-sm flex items-start space-x-2">
                        <span className="font-bold text-blue-600">{String.fromCharCode(65 + index)}.</span>
                        <PreviewContent content={item} className="" />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-600 mb-2">Column 2</h4>
                  <div className="space-y-1">
                    {editedColumn2.split("\n").map((item, index) => (
                      <div key={index} className="p-2 bg-green-50 rounded text-sm flex items-start space-x-2">
                        <span className="font-bold text-green-600">{String.fromCharCode(97 + index)}.</span>
                        <PreviewContent content={item} className="" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )
        )

      case "shortanswer":
      case "integer":
      case "fillinblanks":
        return (
          editedAnswer && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Answer Preview</label>
              <PreviewContent content={editedAnswer} className="p-3 bg-yellow-50 rounded border" />
            </div>
          )
        )

      default:
        return null
    }
  }

  const getQuestionTypeLabel = () => {
    const questionType = actualQuestionType
    const typeLabels = {
      mcq: "MCQ Options",
      multiplecorrect: "Multiple Correct Options",
      truefalse: "True/False Options",
      comprehension: "Comprehension Questions",
      paragraph: "Paragraph Questions",
      assertionreasoning: "Assertion-Reasoning Options",
      matrixmatch: "Matrix Match Options",
      matchfollowing: "Match Following Options",
      shortanswer: "Short Answer",
      integer: "Integer Answer",
      fillinblanks: "Fill in Blanks",
      reasoning: "Reasoning Options",
      casestudy: "Case Study Options",
    }
    return typeLabels[questionType] || "Options"
  }

  const shouldShowOptions = () => {
    const questionType = actualQuestionType?.toLowerCase()
    const optionTypes = [
      "mcq",
      "multiplecorrect",
      "truefalse",
      "assertionreasoning",
      "matrixmatch",
      "matchfollowing",
      "reasoning",
      "casestudy",
      "comprehension",
      "paragraph",
    ]
    return optionTypes.includes(questionType)
  }

  if (!showModal || !question) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white w-screen h-screen overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Question {question.question_number || ""}</h2>
            <div className="mt-1 flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {actualQuestionType || "MCQ"}
              </span>
              <button
                onClick={loadImageLibrary}
                disabled={imageLibraryLoading}
                className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
              >
                <Library className="h-3 w-3" />
                <span>{imageLibraryLoading ? "Loading..." : `Library (${availableImages?.length || 0})`}</span>
              </button>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 grid grid-cols-2 gap-6 p-6 overflow-y-auto">
          {/* Left Column: Edit Area */}
          <div className="space-y-6">
            {renderQuestionTypeFields()}

            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Text (LaTeX)</label>
              <div
                className="relative"
                onMouseEnter={() => setHoveredField("questionText")}
                onMouseLeave={() => setHoveredField(null)}
              >
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-40"
                  value={editedQuestionText}
                  onChange={(e) => setEditedQuestionText(e.target.value)}
                  placeholder="Enter question text in LaTeX"
                />
                <button
                  type="button"
                  onClick={() => openMathExpressionBuilder("questionText")}
                  className="absolute top-2 right-2 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                  title="Add Special Symbols"
                >
                  <Sigma className="h-5 w-5" />
                </button>
                <AIRenderButton fieldType="questionText" className="top-2 right-12" />
                <ImageLibraryButton fieldType="questionText" className="top-2 right-[88px]" />
              </div>
            </div>

            {shouldShowOptions() && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    {getQuestionTypeLabel()} ({Object.keys(editedOptions).length}/4)
                  </label>
                  {canAddMoreOptions() && getAvailableOptions().length > 0 && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Add missing options:</span>
                      {getAvailableOptions().map((optionKey) => (
                        <button
                          key={optionKey}
                          onClick={() => handleAddOption(optionKey)}
                          className="inline-flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        >
                          <Plus className="h-3 w-3" />
                          <span>{optionKey.toUpperCase()}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {Object.entries(editedOptions).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      {/* Option Input */}
                      <div
                        className="flex items-center space-x-2 relative"
                        onMouseEnter={() => setHoveredField(`option_${key}`)}
                        onMouseLeave={() => setHoveredField(null)}
                      >
                        <span className="font-bold text-blue-600 uppercase text-lg">{key}.</span>
                        <input
                          type="text"
                          className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-32"
                          value={value}
                          onChange={(e) => handleOptionChange(key, e.target.value)}
                          placeholder={`Option ${key.toUpperCase()}`}
                        />
                        <button
                          type="button"
                          onClick={() => openMathExpressionBuilder(`option_${key}`)}
                          className="absolute top-1/2 -translate-y-1/2 right-16 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                          title="Add Special Symbols"
                        >
                          <Sigma className="h-4 w-4" />
                        </button>
                        <AIRenderButton fieldType={`option_${key}`} className="top-1/2 -translate-y-1/2 right-8" />
                        <ImageLibraryButton
                          fieldType={`option_${key}`}
                          className="top-1/2 -translate-y-1/2 right-[88px]"
                        />
                        <button
                          onClick={() => handleRemoveOption(key)}
                          className="text-red-500 hover:text-red-700"
                          title={`Remove option ${key.toUpperCase()}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Correct Answer Selection */}
                      <div className="flex items-center space-x-2 ml-6">
                        {actualQuestionType === "multiplecorrect" ? (
                          <>
                            <input
                              type="checkbox"
                              id={`correct-${key}`}
                              value={key}
                              checked={selectedCorrectAnswers.includes(key.toUpperCase())}
                              onChange={() => handleCorrectAnswerChange(key)}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor={`correct-${key}`}
                              className={`text-sm cursor-pointer ${
                                selectedCorrectAnswers.includes(key.toUpperCase())
                                  ? "text-green-700 font-medium"
                                  : "text-gray-600"
                              }`}
                            >
                              {selectedCorrectAnswers.includes(key.toUpperCase()) ? (
                                <span className="flex items-center space-x-1">
                                  <CheckCircle className="h-4 w-4" />
                                  <span>Correct Answer</span>
                                </span>
                              ) : (
                                "Mark as correct answer"
                              )}
                            </label>
                          </>
                        ) : (
                          <>
                            <input
                              type="radio"
                              id={`correct-${key}`}
                              name="correctAnswer"
                              value={key}
                              checked={selectedCorrectAnswer === key}
                              onChange={() => handleCorrectAnswerChange(key)}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                            />
                            <label
                              htmlFor={`correct-${key}`}
                              className={`text-sm cursor-pointer ${
                                selectedCorrectAnswer === key ? "text-green-700 font-medium" : "text-gray-600"
                              }`}
                            >
                              {selectedCorrectAnswer === key ? (
                                <span className="flex items-center space-x-1">
                                  <CheckCircle className="h-4 w-4" />
                                  <span>Correct Answer</span>
                                </span>
                              ) : (
                                "Mark as correct answer"
                              )}
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {Object.keys(editedOptions).length === 0 && (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    <p>No options available. Add some options using the buttons above.</p>
                  </div>
                )}

                {/* Correct Answer Status */}
                {Object.keys(editedOptions).length > 0 && (
                  <div
                    className={`mt-4 p-3 rounded-lg border ${
                      (
                        actualQuestionType === "multiplecorrect"
                          ? selectedCorrectAnswers.length > 0
                          : selectedCorrectAnswer
                      )
                        ? "bg-green-50 border-green-200"
                        : "bg-amber-50 border-amber-200"
                    }`}
                  >
                    {(
                      actualQuestionType === "multiplecorrect"
                        ? selectedCorrectAnswers.length > 0
                        : selectedCorrectAnswer
                    ) ? (
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          {actualQuestionType === "multiplecorrect"
                            ? `Correct answers selected: ${selectedCorrectAnswers.join(", ")}`
                            : `Correct answer selected: Option ${selectedCorrectAnswer.toUpperCase()}`}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-amber-800">
                          ⚠️ Please select{" "}
                          {actualQuestionType === "multiplecorrect"
                            ? "the correct answers"
                            : "which option is the correct answer"}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* References */}
            {editedReferences.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">References</label>
                <div className="space-y-3">
                  {editedReferences.map((ref, index) => (
                    <div key={index} className="flex items-center space-x-2 relative">
                      <input
                        type="text"
                        className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-20"
                        value={ref.text}
                        onChange={(e) => handleReferenceChange(index, e.target.value)}
                        placeholder="Reference text"
                      />
                      <button
                        type="button"
                        onClick={() => openMathExpressionBuilder(`reference_${index}`)}
                        className="absolute top-1/2 -translate-y-1/2 right-8 p-1 rounded-full bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                        title="Add Special Symbols"
                      >
                        <Sigma className="h-4 w-4" />
                      </button>
                      <ImageLibraryButton
                        fieldType={`reference_${index}`}
                        className="top-1/2 -translate-y-1/2 right-[88px]"
                      />
                      <button onClick={() => handleRemoveReference(index)} className="text-red-500 hover:text-red-700">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleAddReference}
                  className="mt-3 flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Reference</span>
                </button>
              </div>
            )}
          </div>

          {/* Right Column: Live Preview */}
          <div className="space-y-6 bg-gray-50 p-4 rounded-lg border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>

            {renderQuestionTypePreview()}

            {/* Question Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Preview</label>
              <PreviewContent content={editedQuestionText} className="p-3 bg-white rounded border min-h-[100px]" />
            </div>

            {/* Options Preview */}
            {shouldShowOptions() && Object.keys(editedOptions).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Options Preview ({Object.keys(editedOptions).length}/4)
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(editedOptions).map(([key, value]) => (
                    <div
                      key={key}
                      className={`flex items-start space-x-3 p-3 rounded border ${
                        selectedCorrectAnswer === key ? "bg-green-50 border-green-300" : "bg-white"
                      }`}
                    >
                      <span
                        className={`font-bold uppercase text-lg ${
                          selectedCorrectAnswer === key ? "text-green-600" : "text-blue-600"
                        }`}
                      >
                        {key}.
                      </span>
                      <PreviewContent content={value} className="text-gray-700 flex-1" />
                      {selectedCorrectAnswer === key && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex items-center space-x-1">
                          <CheckCircle className="h-3 w-3" />
                          <span>Correct</span>
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Correct Answer Summary */}
                <div
                  className={`mt-3 p-3 rounded-lg ${
                    selectedCorrectAnswer
                      ? "bg-green-100 border border-green-300"
                      : "bg-amber-100 border border-amber-300"
                  }`}
                >
                  {selectedCorrectAnswer ? (
                    <div>
                      <div className="text-sm font-medium text-green-800 mb-1">
                        ✅ Correct Answer: Option {selectedCorrectAnswer.toUpperCase()}
                      </div>
                      <PreviewContent
                        content={editedOptions[selectedCorrectAnswer]}
                        className="text-sm text-green-700"
                      />
                    </div>
                  ) : (
                    <div className="text-sm font-medium text-amber-800">⚠️ No correct answer selected</div>
                  )}
                </div>

                {canAddMoreOptions() && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                    <strong>Note:</strong> You can add {4 - Object.keys(editedOptions).length} more option(s) to reach
                    the maximum of 4 options.
                  </div>
                )}
              </div>
            )}

            {/* References Preview */}
            {editedReferences.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">References Preview</label>
                <div className="space-y-2">
                  {editedReferences.map((ref, index) => (
                    <div key={index} className="bg-white p-3 rounded border">
                      <div
                        className="text-gray-600 text-sm"
                        dangerouslySetInnerHTML={{ __html: renderLatex(ref.text) }}
                      />
                      {ref.page && <div className="text-xs text-gray-400 mt-1">Page {ref.page}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="p-6 border-t flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Ban className="h-4 w-4" />
            <span>Cancel</span>
          </button>
          <button
            onClick={handleSave}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save className="h-4 w-4" />
            <span>Save Changes</span>
          </button>
        </div>
      </div>

      {/* Math Expression Builder Modal */}
      {showMathExpressionBuilder && (
        <AddQuestionMathsExpression
          onClose={() => setShowMathExpressionBuilder(false)}
          onInsert={handleInsertMathExpression}
          fieldType={currentInputTarget}
        />
      )}

      {/* AI Response Modals */}
      {/* AI Response Modals */}
      <AIResponseModal fieldType="questionText" />
      <AIResponseModal fieldType="passage" />
      <AIResponseModal fieldType="assertion" />
      <AIResponseModal fieldType="reason" />
      <AIResponseModal fieldType="answer" />
      {Object.keys(editedOptions).map((key) => (
        <AIResponseModal key={key} fieldType={`option_${key}`} />
      ))}
      <AIResponseModal fieldType="column1" />
      <AIResponseModal fieldType="column2" />

      {renderImageLibraryModal()}
    </div>
  )
}

export default QuestionEditorModal
